/**
 * Timezone formatting for user dashboard charts.
 * Converts UTC timestamps to the chart's selected timezone (e.g. EST) for subtitle and "Last updated" display.
 */

const IANA_TIMEZONE_MAP: { [key: string]: string } = {
  EST: 'America/New_York',
  PST: 'America/Los_Angeles',
  IST: 'Asia/Kolkata',
  GMT: 'Europe/London',
  CST: 'America/Chicago',
  MST: 'America/Denver',
  BST: 'Europe/London',
  CET: 'Europe/Paris',
  EET: 'Europe/Bucharest',
  JST: 'Asia/Tokyo',
  AEST: 'Australia/Sydney',
  GST: 'Asia/Dubai',
  SGT: 'Asia/Singapore',
  NZST: 'Pacific/Auckland',
  UTC: 'UTC',
};

const DISPLAY_ABBR_MAP: { [key: string]: string } = {
  'America/New_York': 'EST',
  'America/Los_Angeles': 'PST',
  'Asia/Kolkata': 'IST',
  'Europe/London': 'GMT',
  'America/Chicago': 'CST',
  'America/Denver': 'MST',
  'Europe/Paris': 'CET',
  'Asia/Tokyo': 'JST',
  'Australia/Sydney': 'AEST',
  'Asia/Dubai': 'GST',
  'Asia/Singapore': 'SGT',
  'Pacific/Auckland': 'NZST',
};

export function getIanaTimezone(abbr: string): string {
  const trimmed = (abbr || '').trim();
  if (!trimmed) return 'UTC';
  return IANA_TIMEZONE_MAP[trimmed.toUpperCase()] || trimmed;
}

export function getTimeZoneDisplayAbbr(abbrOrIana: string): string {
  const trimmed = (abbrOrIana || '').trim();
  if (!trimmed) return 'UTC';
  const upper = trimmed.toUpperCase();
  if (IANA_TIMEZONE_MAP[upper]) return upper;
  if (DISPLAY_ABBR_MAP[trimmed]) return DISPLAY_ABBR_MAP[trimmed];
  if (trimmed.length <= 4 && /^[A-Z]+$/.test(upper)) return upper;
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: trimmed, timeZoneName: 'short' })
      .formatToParts(new Date());
    const tzPart = parts.find(p => p.type === 'timeZoneName');
    return tzPart?.value || trimmed;
  } catch {
    return trimmed;
  }
}

/**
 * Format a date in the given timezone for "Last updated" style: "Jan 19, 2026 11:36:00 EST"
 */
export function formatDateTimeInTimezone(date: Date, timezoneAbbr: string): string {
  const iana = getIanaTimezone(timezoneAbbr);
  const displayAbbr = getTimeZoneDisplayAbbr(timezoneAbbr);

  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: iana,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });

  const parts = formatter.formatToParts(date);
  const month = parts.find(p => p.type === 'month')?.value ?? '';
  const day = parts.find(p => p.type === 'day')?.value ?? '';
  const year = parts.find(p => p.type === 'year')?.value ?? '';
  const hour = parts.find(p => p.type === 'hour')?.value ?? '00';
  const minute = parts.find(p => p.type === 'minute')?.value ?? '00';
  const second = parts.find(p => p.type === 'second')?.value ?? '00';

  return `${month} ${day}, ${year} ${hour}:${minute}:${second} ${displayAbbr}`;
}

/**
 * Format a date in the given timezone for subtitle style: "10 Feb 2025 3:21:28 PM EST"
 */
export function formatUserFriendlyDateTimeInTimezone(date: Date, timezoneAbbr: string): string {
  const iana = getIanaTimezone(timezoneAbbr);
  const displayAbbr = getTimeZoneDisplayAbbr(timezoneAbbr);

  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: iana,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });

  const parts = formatter.formatToParts(date);
  const month = parts.find(p => p.type === 'month')?.value ?? '';
  const day = parts.find(p => p.type === 'day')?.value ?? '';
  const year = parts.find(p => p.type === 'year')?.value ?? '';
  const hour = parts.find(p => p.type === 'hour')?.value ?? '12';
  const minute = parts.find(p => p.type === 'minute')?.value ?? '00';
  const second = parts.find(p => p.type === 'second')?.value ?? '00';
  const dayPeriod = parts.find(p => p.type === 'dayPeriod')?.value ?? 'AM';

  return `${day} ${month} ${year} ${hour}:${minute}:${second} ${dayPeriod} ${displayAbbr}`;
}
